import { useState, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import { getMonthlyStats, saveMonthlyStats, getMonthName } from "@/lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { 
  Save, 
  Users, 
  Bug, 
  Droplets, 
  Syringe, 
  HandMetal, 
  ShieldCheck, 
  AlertTriangle,
  Phone,
  Loader2
} from "lucide-react";

const MONTHS = [
  { value: 1, label: "January" },
  { value: 2, label: "February" },
  { value: 3, label: "March" },
  { value: 4, label: "April" },
  { value: 5, label: "May" },
  { value: 6, label: "June" },
  { value: 7, label: "July" },
  { value: 8, label: "August" },
  { value: 9, label: "September" },
  { value: 10, label: "October" },
  { value: 11, label: "November" },
  { value: 12, label: "December" },
];

const YEARS = [2024, 2025, 2026, 2027];

export default function MonthlyEntry() {
  const [searchParams] = useSearchParams();
  const currentDate = new Date();
  
  // Get month/year from URL params or default to current
  const initialMonth = parseInt(searchParams.get('month')) || currentDate.getMonth() + 1;
  const initialYear = parseInt(searchParams.get('year')) || currentDate.getFullYear();
  
  const [year, setYear] = useState(initialYear);
  const [month, setMonth] = useState(initialMonth);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [existingData, setExistingData] = useState(false);
  const [rates, setRates] = useState({});
  
  const [formData, setFormData] = useState({
    patient_days: 0,
    admissions: 0,
    cauti_count: 0,
    suti_count: 0,
    abuti_count: 0,
    clabsi_count: 0,
    primary_bsi_count: 0,
    skin_soft_tissue_count: 0,
    pneumonia_count: 0,
    cdiff_count: 0,
    mdro_count: 0,
    ho_mdro_labid_count: 0,
    ho_cdi_labid_count: 0,
    urinary_catheter_days: 0,
    central_line_days: 0,
    hand_hygiene_compliant: 0,
    hand_hygiene_total: 0,
    isolation_ppe_compliant: 0,
    isolation_ppe_total: 0,
    ic_eoc_compliant: 0,
    ic_eoc_total: 0,
    dialysis_eoc_compliant: 0,
    dialysis_eoc_total: 0,
    sticks: 0,
    sharps: 0,
    splashes: 0,
    respiratory_callins: 0,
    gi_callins: 0,
    skin_callins: 0,
    other_callins: 0,
    notes: "",
  });

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        const result = await getMonthlyStats(year, month);
        if (result.exists) {
          const stats = result.stats;
          setFormData({
            patient_days: stats.patient_days || 0,
            admissions: stats.admissions || 0,
            cauti_count: stats.cauti_count || 0,
            suti_count: stats.suti_count || 0,
            abuti_count: stats.abuti_count || 0,
            clabsi_count: stats.clabsi_count || 0,
            primary_bsi_count: stats.primary_bsi_count || 0,
            skin_soft_tissue_count: stats.skin_soft_tissue_count || 0,
            pneumonia_count: stats.pneumonia_count || 0,
            cdiff_count: stats.cdiff_count || 0,
            mdro_count: stats.mdro_count || 0,
            ho_mdro_labid_count: stats.ho_mdro_labid_count || 0,
            ho_cdi_labid_count: stats.ho_cdi_labid_count || 0,
            urinary_catheter_days: stats.urinary_catheter_days || 0,
            central_line_days: stats.central_line_days || 0,
            hand_hygiene_compliant: stats.hand_hygiene_compliant || 0,
            hand_hygiene_total: stats.hand_hygiene_total || 0,
            isolation_ppe_compliant: stats.isolation_ppe_compliant || 0,
            isolation_ppe_total: stats.isolation_ppe_total || 0,
            ic_eoc_compliant: stats.ic_eoc_compliant || 0,
            ic_eoc_total: stats.ic_eoc_total || 0,
            dialysis_eoc_compliant: stats.dialysis_eoc_compliant || 0,
            dialysis_eoc_total: stats.dialysis_eoc_total || 0,
            sticks: stats.sticks || 0,
            sharps: stats.sharps || 0,
            splashes: stats.splashes || 0,
            respiratory_callins: stats.respiratory_callins || 0,
            gi_callins: stats.gi_callins || 0,
            skin_callins: stats.skin_callins || 0,
            other_callins: stats.other_callins || 0,
            notes: stats.notes || "",
          });
          setExistingData(true);
        } else {
          // Reset form for new entry
          setFormData({
            patient_days: 0,
            admissions: 0,
            cauti_count: 0,
            suti_count: 0,
            abuti_count: 0,
            clabsi_count: 0,
            primary_bsi_count: 0,
            skin_soft_tissue_count: 0,
            pneumonia_count: 0,
            cdiff_count: 0,
            mdro_count: 0,
            ho_mdro_labid_count: 0,
            ho_cdi_labid_count: 0,
            urinary_catheter_days: 0,
            central_line_days: 0,
            hand_hygiene_compliant: 0,
            hand_hygiene_total: 0,
            isolation_ppe_compliant: 0,
            isolation_ppe_total: 0,
            ic_eoc_compliant: 0,
            ic_eoc_total: 0,
            dialysis_eoc_compliant: 0,
            dialysis_eoc_total: 0,
            sticks: 0,
            sharps: 0,
            splashes: 0,
            respiratory_callins: 0,
            gi_callins: 0,
            skin_callins: 0,
            other_callins: 0,
            notes: "",
          });
          setExistingData(false);
        }
        setRates(result.rates || {});
      } catch (error) {
        console.error("Failed to fetch monthly stats:", error);
        toast.error("Failed to load data");
      }
      setLoading(false);
    };
    fetchData();
  }, [year, month]);

  const handleChange = (field, value) => {
    setFormData((prev) => ({
      ...prev,
      [field]: field === "notes" ? value : parseInt(value) || 0,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await saveMonthlyStats({
        year,
        month,
        ...formData,
      });
      setExistingData(true);
      toast.success(`Data saved for ${getMonthName(month)} ${year}`);
      
      // Refresh rates
      const result = await getMonthlyStats(year, month);
      setRates(result.rates || {});
    } catch (error) {
      console.error("Failed to save:", error);
      toast.error("Failed to save data");
    }
    setSaving(false);
  };

  const InputField = ({ label, field, icon: Icon }) => (
    <div className="space-y-1.5">
      <Label htmlFor={field} className="text-xs font-medium text-slate-600 flex items-center gap-1.5">
        {Icon && <Icon className="w-3.5 h-3.5 text-slate-400" />}
        {label}
      </Label>
      <Input
        id={field}
        type="number"
        min="0"
        value={formData[field]}
        onChange={(e) => handleChange(field, e.target.value)}
        className="h-9 font-mono text-sm"
        data-testid={`input-${field}`}
      />
    </div>
  );

  return (
    <div className="max-w-6xl mx-auto space-y-6">
      {/* Header with Month/Year Selection */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
            Monthly Data Entry
          </h1>
          <p className="text-sm text-slate-500 mt-1">
            {existingData ? "Editing existing data" : "New entry"} for {getMonthName(month)} {year}
          </p>
        </div>
        <div className="flex gap-3">
          <Select value={String(month)} onValueChange={(v) => setMonth(Number(v))}>
            <SelectTrigger className="w-36" data-testid="month-selector">
              <SelectValue placeholder="Month" />
            </SelectTrigger>
            <SelectContent>
              {MONTHS.map((m) => (
                <SelectItem key={m.value} value={String(m.value)}>{m.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select value={String(year)} onValueChange={(v) => setYear(Number(v))}>
            <SelectTrigger className="w-28" data-testid="year-selector">
              <SelectValue placeholder="Year" />
            </SelectTrigger>
            <SelectContent>
              {YEARS.map((y) => (
                <SelectItem key={y} value={String(y)}>{y}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      {loading ? (
        <div className="flex items-center justify-center h-64">
          <Loader2 className="w-8 h-8 animate-spin text-sky-600" />
        </div>
      ) : (
        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Patient Days & Admissions */}
          <Card className="border-slate-200 shadow-sm">
            <CardHeader className="pb-3 bg-slate-50 border-b border-slate-100">
              <CardTitle className="text-sm font-semibold text-sky-700 flex items-center gap-2 uppercase tracking-wide">
                <Users className="w-4 h-4" />
                Patient Days & Admissions
              </CardTitle>
            </CardHeader>
            <CardContent className="pt-4">
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <InputField label="Patient Days" field="patient_days" icon={Users} />
                <InputField label="Admissions" field="admissions" icon={Users} />
                <InputField label="Urinary Catheter Days" field="urinary_catheter_days" icon={Droplets} />
                <InputField label="Central Line Days" field="central_line_days" icon={Syringe} />
              </div>
            </CardContent>
          </Card>

          {/* Hospital Acquired Infections */}
          <Card className="border-slate-200 shadow-sm">
            <CardHeader className="pb-3 bg-slate-50 border-b border-slate-100">
              <CardTitle className="text-sm font-semibold text-sky-700 flex items-center gap-2 uppercase tracking-wide">
                <Bug className="w-4 h-4" />
                Hospital Acquired Infections
              </CardTitle>
            </CardHeader>
            <CardContent className="pt-4">
              <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
                <InputField label="CAUTI" field="cauti_count" />
                <InputField label="SUTI (non-catheter)" field="suti_count" />
                <InputField label="ABUTI (non-catheter)" field="abuti_count" />
                <InputField label="CLABSI" field="clabsi_count" />
                <InputField label="Primary BSI" field="primary_bsi_count" />
                <InputField label="Skin/Soft Tissue" field="skin_soft_tissue_count" />
                <InputField label="Pneumonia" field="pneumonia_count" />
                <InputField label="C. Diff" field="cdiff_count" />
                <InputField label="All MDROs" field="mdro_count" />
              </div>
            </CardContent>
          </Card>

          {/* Hospital-Onset LabID Events */}
          <Card className="border-slate-200 shadow-sm">
            <CardHeader className="pb-3 bg-slate-50 border-b border-slate-100">
              <CardTitle className="text-sm font-semibold text-sky-700 flex items-center gap-2 uppercase tracking-wide">
                <Bug className="w-4 h-4" />
                Hospital-Onset LabID Events
              </CardTitle>
            </CardHeader>
            <CardContent className="pt-4">
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <InputField label="HO-MDRO LabID" field="ho_mdro_labid_count" />
                <InputField label="HO-CDI LabID" field="ho_cdi_labid_count" />
              </div>
            </CardContent>
          </Card>

          {/* Compliance Observations */}
          <Card className="border-slate-200 shadow-sm">
            <CardHeader className="pb-3 bg-slate-50 border-b border-slate-100">
              <CardTitle className="text-sm font-semibold text-sky-700 flex items-center gap-2 uppercase tracking-wide">
                <ShieldCheck className="w-4 h-4" />
                Compliance Observations
              </CardTitle>
            </CardHeader>
            <CardContent className="pt-4 space-y-6">
              {/* Hand Hygiene */}
              <div>
                <h4 className="text-xs font-medium text-slate-500 mb-3 flex items-center gap-2">
                  <HandMetal className="w-3.5 h-3.5" />
                  Hand Hygiene Compliance
                </h4>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                  <InputField label="Compliant Observations" field="hand_hygiene_compliant" />
                  <InputField label="Total Observations" field="hand_hygiene_total" />
                  <div className="flex items-end">
                    <div className="p-3 bg-sky-50 rounded-lg w-full">
                      <p className="text-xs text-sky-600">Calculated Rate</p>
                      <p className="text-lg font-bold text-sky-700 font-mono">
                        {rates.hand_hygiene_rate || 0}%
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Isolation PPE */}
              <div>
                <h4 className="text-xs font-medium text-slate-500 mb-3 flex items-center gap-2">
                  <ShieldCheck className="w-3.5 h-3.5" />
                  Isolation PPE Compliance
                </h4>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                  <InputField label="Compliant Observations" field="isolation_ppe_compliant" />
                  <InputField label="Total Observations" field="isolation_ppe_total" />
                  <div className="flex items-end">
                    <div className="p-3 bg-sky-50 rounded-lg w-full">
                      <p className="text-xs text-sky-600">Calculated Rate</p>
                      <p className="text-lg font-bold text-sky-700 font-mono">
                        {rates.isolation_ppe_rate || 0}%
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              {/* IC EOC Rounds */}
              <div>
                <h4 className="text-xs font-medium text-slate-500 mb-3">IC Environment of Care Rounds</h4>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                  <InputField label="Compliant Observations" field="ic_eoc_compliant" />
                  <InputField label="Total Observations" field="ic_eoc_total" />
                  <div className="flex items-end">
                    <div className="p-3 bg-sky-50 rounded-lg w-full">
                      <p className="text-xs text-sky-600">Calculated Rate</p>
                      <p className="text-lg font-bold text-sky-700 font-mono">
                        {rates.ic_eoc_rate || 0}%
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Dialysis EOC */}
              <div>
                <h4 className="text-xs font-medium text-slate-500 mb-3">Dialysis Environment of Care Rounds</h4>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                  <InputField label="Compliant Observations" field="dialysis_eoc_compliant" />
                  <InputField label="Total Observations" field="dialysis_eoc_total" />
                  <div className="flex items-end">
                    <div className="p-3 bg-sky-50 rounded-lg w-full">
                      <p className="text-xs text-sky-600">Calculated Rate</p>
                      <p className="text-lg font-bold text-sky-700 font-mono">
                        {rates.dialysis_eoc_rate || 0}%
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Bloodborne Pathogen Exposures */}
          <Card className="border-slate-200 shadow-sm">
            <CardHeader className="pb-3 bg-slate-50 border-b border-slate-100">
              <CardTitle className="text-sm font-semibold text-sky-700 flex items-center gap-2 uppercase tracking-wide">
                <AlertTriangle className="w-4 h-4" />
                Bloodborne Pathogen Exposures
              </CardTitle>
            </CardHeader>
            <CardContent className="pt-4">
              <div className="grid grid-cols-3 md:grid-cols-4 gap-4">
                <InputField label="Sticks" field="sticks" />
                <InputField label="Sharps" field="sharps" />
                <InputField label="Splashes" field="splashes" />
                <div className="flex items-end">
                  <div className="p-3 bg-amber-50 rounded-lg w-full">
                    <p className="text-xs text-amber-600">Total</p>
                    <p className="text-lg font-bold text-amber-700 font-mono">
                      {(formData.sticks || 0) + (formData.sharps || 0) + (formData.splashes || 0)}
                    </p>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Employee Call-ins */}
          <Card className="border-slate-200 shadow-sm">
            <CardHeader className="pb-3 bg-slate-50 border-b border-slate-100">
              <CardTitle className="text-sm font-semibold text-sky-700 flex items-center gap-2 uppercase tracking-wide">
                <Phone className="w-4 h-4" />
                Employee Call-ins for Communicable Illnesses
              </CardTitle>
            </CardHeader>
            <CardContent className="pt-4">
              <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
                <InputField label="Respiratory" field="respiratory_callins" />
                <InputField label="Gastrointestinal" field="gi_callins" />
                <InputField label="Skin" field="skin_callins" />
                <InputField label="Other" field="other_callins" />
                <div className="flex items-end">
                  <div className="p-3 bg-slate-100 rounded-lg w-full">
                    <p className="text-xs text-slate-600">Total</p>
                    <p className="text-lg font-bold text-slate-700 font-mono">
                      {(formData.respiratory_callins || 0) + (formData.gi_callins || 0) + 
                       (formData.skin_callins || 0) + (formData.other_callins || 0)}
                    </p>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Notes */}
          <Card className="border-slate-200 shadow-sm">
            <CardHeader className="pb-3 bg-slate-50 border-b border-slate-100">
              <CardTitle className="text-sm font-semibold text-sky-700 uppercase tracking-wide">
                Notes
              </CardTitle>
            </CardHeader>
            <CardContent className="pt-4">
              <Textarea
                value={formData.notes}
                onChange={(e) => handleChange("notes", e.target.value)}
                placeholder="Add any additional notes for this month..."
                className="min-h-24"
                data-testid="input-notes"
              />
            </CardContent>
          </Card>

          {/* Save Button */}
          <div className="flex justify-end gap-4 sticky bottom-4 bg-slate-50 p-4 -mx-4 rounded-lg border border-slate-200 shadow-lg">
            <Button
              type="submit"
              disabled={saving}
              className="bg-sky-600 hover:bg-sky-700 text-white shadow-sm px-8"
              data-testid="save-monthly-data-btn"
            >
              {saving ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Saving...
                </>
              ) : (
                <>
                  <Save className="w-4 h-4 mr-2" />
                  {existingData ? "Update Data" : "Save Data"}
                </>
              )}
            </Button>
          </div>
        </form>
      )}
    </div>
  );
}
